import type { IncomingMessage } from 'http';

import { DEFAULT_LOCALE, isSupportedLocale, LOCALE_COOKIE, SupportedLocale } from './settings';

// 'iw' is the legacy ISO tag some browsers still send for Hebrew.
const LEGACY_TAGS: Record<string, SupportedLocale> = { iw: 'he' };

const normalizeTag = (tag: string): SupportedLocale | undefined => {
  const base = tag.toLowerCase().split('-')[0];
  if (LEGACY_TAGS[base]) return LEGACY_TAGS[base];
  return isSupportedLocale(base) ? base : undefined;
};

export const matchAcceptLanguage = (header?: string | null): SupportedLocale | undefined => {
  if (!header) return undefined;
  const tags = header
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const qParam = params.find((p) => p.trim().startsWith('q='));
      const q = qParam ? parseFloat(qParam.trim().slice(2)) : 1;
      return { tag: tag.trim(), q: Number.isNaN(q) ? 0 : q };
    })
    .filter(({ tag, q }) => tag && q > 0)
    .sort((a, b) => b.q - a.q);

  for (const { tag } of tags) {
    const locale = normalizeTag(tag);
    if (locale) return locale;
  }
  return undefined;
};

export const matchNavigatorLanguages = (languages: readonly string[]): SupportedLocale | undefined => {
  for (const tag of languages) {
    const locale = normalizeTag(tag);
    if (locale) return locale;
  }
  return undefined;
};

const parseCookieHeader = (cookieHeader?: string): string | undefined => {
  if (!cookieHeader) return undefined;
  const match = cookieHeader
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${LOCALE_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(LOCALE_COOKIE.length + 1)) : undefined;
};

export interface DetectedLocale {
  locale: SupportedLocale;
  // true when neither an explicit choice (query/cookie) nor the browser
  // languages produced a match — the client should try the geo-IP fallback.
  needsGeoFallback: boolean;
}

export const detectLocale = (
  query: Record<string, string | string[] | undefined>,
  req?: IncomingMessage,
): DetectedLocale => {
  const queryLang = Array.isArray(query.lang) ? query.lang[0] : query.lang;
  if (isSupportedLocale(queryLang)) {
    return { locale: queryLang, needsGeoFallback: false };
  }

  let cookieHeader: string | undefined;
  if (req) {
    cookieHeader = req.headers.cookie;
  } else if (typeof document !== 'undefined') {
    cookieHeader = document.cookie;
  }
  const cookieLocale = parseCookieHeader(cookieHeader);
  if (isSupportedLocale(cookieLocale)) {
    return { locale: cookieLocale, needsGeoFallback: false };
  }

  let acceptLanguage: string | undefined;
  if (req) {
    acceptLanguage = req.headers['accept-language'] as string | undefined;
  } else if (typeof navigator !== 'undefined') {
    acceptLanguage = navigator.languages.join(',');
  }
  const browserLocale = matchAcceptLanguage(acceptLanguage);
  if (browserLocale) {
    return { locale: browserLocale, needsGeoFallback: false };
  }

  return { locale: DEFAULT_LOCALE, needsGeoFallback: true };
};
